import zlibInflate from 'pako/lib/zlib/inflate.js';
import ZStream from 'pako/lib/zlib/zstream.js';

const MAX_IMAGE_BYTES = 128 * 1024 * 1024;
const value = (params, short, long) => (params.get(short) ?? params.get(long))?.value;

// Encoded image bytes can literally contain " EI /Name Do ". For raw and
// Flate images the byte boundary is knowable; never use an operator heuristic.
export function exactInlineImageEnd(scanner, params) {
  const start = scanner.position;
  let filter = params.get('F') ?? params.get('Filter');
  if (filter?.type === 'array') filter = filter.items[0];
  const codec = filter?.value;
  let length;
  if (!codec) {
    const width = value(params, 'W', 'Width'), height = value(params, 'H', 'Height');
    const mask = value(params, 'IM', 'ImageMask');
    const bits = mask ? 1 : value(params, 'BPC', 'BitsPerComponent');
    const space = value(params, 'CS', 'ColorSpace');
    const components = mask ? 1 : ({ G: 1, DeviceGray: 1, RGB: 3, DeviceRGB: 3, CMYK: 4, DeviceCMYK: 4, I: 1, Indexed: 1 }[space] || scanner.colorComponents?.(space));
    if (!components || ![1, 2, 4, 8, 16].includes(bits) || ![width, height].every(n => Number.isSafeInteger(n) && n > 0)) {
      throw new Error('An inline image has invalid or unsupported dimensions. No changes were saved.');
    }
    length = Math.ceil(width * components * bits / 8) * height;
    if (!Number.isSafeInteger(length) || length > MAX_IMAGE_BYTES) throw new Error('This PDF contains an inline image too large to edit safely.');
  } else if (codec === 'Fl' || codec === 'FlateDecode') {
    const stream = new ZStream();
    if (zlibInflate.inflateInit(stream) !== 0) throw new Error('The image decoder could not start.');
    try {
      stream.input = scanner.bytes.subarray(start); stream.avail_in = stream.input.length;
      stream.output = new Uint8Array(65536);
      let status = 0;
      do {
        stream.next_out = 0; stream.avail_out = stream.output.length;
        status = zlibInflate.inflate(stream, 0);
        if (stream.total_out > MAX_IMAGE_BYTES) throw new Error('This PDF contains an inline image too large to edit safely.');
      } while (status === 0 && stream.avail_in > 0);
      if (status !== 1) throw new Error('An inline image is damaged. No changes were saved.');
      length = stream.total_in;
    } finally { zlibInflate.inflateEnd(stream); }
  } else if (codec === 'RL' || codec === 'RunLengthDecode') {
    let pos = start;
    while (pos < scanner.bytes.length) {
      const count = scanner.bytes[pos++];
      if (count === 128) { length = pos - start; break; }
      pos += count < 128 ? count + 1 : 1;
    }
    if (length === undefined) throw new Error('An inline image is incomplete. No changes were saved.');
  } else if (['DCT', 'DCTDecode', 'A85', 'ASCII85Decode', 'AHx', 'ASCIIHexDecode'].includes(codec)) {
    return null; // These encodings have explicit EOD markers in the tokenizer.
  } else {
    throw new Error('This PDF uses an inline image encoding that cannot be isolated safely. No changes were saved.');
  }
  const end = start + length;
  let pos = end;
  while ([0, 9, 10, 12, 13, 32].includes(scanner.bytes[pos])) pos++;
  if (scanner.bytes[pos] !== 69 || scanner.bytes[pos + 1] !== 73 ||
      pos + 2 < scanner.bytes.length && ![0, 9, 10, 12, 13, 32].includes(scanner.bytes[pos + 2])) {
    throw new Error('An inline image has no valid end marker. No changes were saved.');
  }
  scanner.moveTo(pos + 2);
  return scanner.bytes.subarray(start, end);
}
